// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { avoidBreakInsidePrintElement, styledScrollbar, xScreen } from "@guestsupply/XDL/Utilities";
import color from "@guestsupply/XDL/Utilities/color";
import { mergeWithCss } from "@guestsupply/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import baseTheme from "@insite/mobius/globals/baseTheme";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp, resolveColor } from "@insite/mobius/utilities";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import styled, { css } from "styled-components";

export const styledLinksMenu = (width: number) => {
    const xColor = {
        background: {
            hover: "common.ui4",
        },
        text: {
            default: "text",
            hover: "text",
        },
    };

    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}

        padding: 5px 0 0;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 4px;
        ${StyledClickable} {
            display: flex;
            width: 100%;
            ${/* sc-selector */ StyledTypography} {
                font-size: 13px;
                padding: 10px 15px;
                color: ${resolveColor(xColor.text.default, theme)};
                font-weight: normal;
                text-transform: none;
                letter-spacing: 0;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor(xColor.background.hover, theme)};
                color: ${resolveColor(xColor.text.hover, theme)};
            }
        }

        ${styledScrollbar()}
    `;
};

/**
 * Hide input increment/decrement arrows from browser when textField type = number
 * */
export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
    }
`;

export const companyLogoItem: any = {
    css: css`
        display: none;
        padding: 0;
        width: 125px;
        float: left; // for print only

        @media print {
            display: block;
            padding-bottom: 20px;
            -webkit-padding-start: 15px;
            padding-left: 15px;
            margin-top: 15px;
        }

        img {
            display: block;
            max-width: 100%;
            height: auto;
            padding-left: 0;
            margin-left: 0;
        }
    `,
};

export const createLinkIconStyles = (icon: React.ComponentType | string, linkStyles?: LinkPresentationProps) => {
    const iconPosition = linkStyles?.icon?.position || "right";
    const marginPosition = iconPosition === "left" ? "right" : "left";
    return {
        ...mergeWithCss(
            {
                typographyProps: {
                    size: 14,
                    ellipsis: true,
                    weight: "bold",
                    css: css`
                        letter-spacing: 0;
                        line-height: 16px;
                    `,
                },
                icon: {
                    iconProps: {
                        src: icon,
                        size: 18,
                        color: "text.link",
                        css: css`
                            opacity: 0.33;
                            ${marginPosition === "left" ? "margin-left: 8px;" : "margin-right: 8px;"}
                        `,
                    },
                    position: iconPosition,
                },
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const circleListStyle = css`
    &:before {
        content: "";
        display: inline-block;
        width: 8px;
        min-width: 8px;
        max-width: 8px;
        height: 8px;
        min-height: 8px;
        max-height: 8px;
        border-radius: 50%;
        background-color: ${color("common.ui8")};
        margin-top: 8px;
        margin-right: 8px;
    }
`;

export const pageTitleStyle: TypographyProps = {
    variant: "h1",
    size: 32,
    weight: 900,
    css: css`
        line-height: 40px;
        margin-bottom: 20px;
        display: block;
    `,
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: flex-end;
            flex-wrap: wrap;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-right: 0;
                margin-top: 10px;
                order: 1;
            }
        `,
    },
    submitButton: {
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                order: 0;
            }
        `,
    },
};

// This Wrapper used For total display ( order total, cart total, ...)
export const StyledBox = styled(StyledWrapper)`
    width: 100%;
    display: flex;
    padding: 17px;
    border: 1px solid ${color("common.border")};
    background-color: ${color("common.ui1")};
    @media print {
        max-width: 400px;
    }
    ${avoidBreakInsidePrintElement}
`;
export const homePageTitleStyle: TypographyProps = {
    variant: "h2",
    color: "text.main",
    transform: "capitalize",
    css: css`
        margin-bottom: 0;
    `,
};

export const mobileMenuLinkStyle: LinkPresentationProps = {
    color: "secondary",
    typographyProps: {
        weight: 500,
        lineHeight: "16px",
        css: css`
            text-transform: uppercase;
        `,
    },
};
export const tippySharedStyles: InjectableCss = {
    css: css`
        .tippy-box {
            background: ${color("common.ui1")};
            color: ${color("text.main")};
            padding: 13px;
            min-width: 300px;
            max-width: 327px !important;
            border-radius: 4px;
            border: 1px solid ${color("common.border")};
        }

        .tippy-box[data-placement^="bottom"] > .tippy-arrow:before {
            border-width: 1px 1px 0 0 !important;
        }
        .tippy-box[data-placement^="top"] > .tippy-arrow:before {
            border-width: 0 0 1px 1px !important;
        }

        .tippy-arrow:before {
            background: ${color("common.ui1")};
            border-color: ${color("common.border")};
            border-width: 0 1px 1px 0 !important;
            transform-origin: unset !important;
            width: 12px;
            height: 12px;
            transform: rotate(-45deg);
        }

        .tippy-content {
            padding: 0;
        }
    `,
};

export const dotBttonStyles: ButtonPresentationProps = {
    css: css`
        height: 10px;
        width: 10px;
        min-height: unset;
        padding: 0;
        margin-right: 8px;
        border: 1px solid ${color("common.ui4")};
        border-radius: 50%;
        background: ${color("common.ui1")};
        &:active,
        &:focus {
            &&&& {
                background: ${color("common.ui9")};
                outline: none;
                border: 0;
            }
        }
        &:hover {
            &&&& {
                background: ${color("common.ui8")};
                outline: none;
                border: 0;
            }
        }
    `,
};
